import { useState, useEffect } from "react";

// react-router components
import { Link, matchPath, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";

// Soft UI Dashboard PRO React example components
import BaseNavbarLink from "../BaseNavbar/BaseNavbarLink";
import BaseNavbarMobile from "../BaseNavbar/BaseNavbarMobile";

// Soft UI Dashboard PRO React base styles
import breakpoints from "assets/theme/base/breakpoints";

// BaseNavbar dropdown menus
import MenuOptions from "./MenuOptions";

// Nucleus Logo
import NucleusLogo from "assets/images/nucleus_logo.png";
import NucleusLogoInverted from "assets/images/nucleus_logo_inverted.png";
import NucleusLogoSmall from "assets/images/nucleus_logo_small.png";
import NucleusLogoSmallInverted from "assets/images/nucleus_logo_small_inverted.png";

import { useSoftUIController } from "hooks/context";
import { useAuth0 } from "@auth0/auth0-react";
import userAPI from "services/api/user";
import { getUserMenuItems } from "utils/setup";
import LogoutButton from "components/Buttons/LogoutButton";
import { Box, List, ListItem, ListItemButton, ListItemText, Menu, Popover, Switch } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { setSelectedOrg } from "hooks/context";
import moment from "moment-timezone";

function popoverDeployment(open, anchorEl, handleClose, user, dispatch, selectedOrg) {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{
        sx: {
          mt: 1, minWidth: '180px', borderRadius: '12px', boxShadow: 'lg',
          zIndex: 999999999, backgroundColor: '#fff'
        }
      }}
    >
      <SoftBox>
        <List sx={{ p: 1 }}>
          {user.organisations.map((org) => (
            <ListItem key={org.organisation_id} disablePadding>
              <ListItemButton
                selected={org.organisation_id === selectedOrg}
                onClick={() => {
                  setSelectedOrg(dispatch, org.organisation_id);
                  moment.tz.setDefault(user.organisations.find(row => row.organisation_id == org.organisation_id).timezone);
                  localStorage.setItem("selectedOrg", org.organisation_id);
                  handleClose();
                }}
                sx={{ borderRadius: '6px' }}
              >
                <ListItemText
                  primary={org.label}
                  primaryTypographyProps={{ fontSize: '14px', fontWeight: org.organisation_id === selectedOrg ? 'bold' : 'normal' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </SoftBox>
    </Popover>
  );
}

function BaseNavbar({ map, routes, user, transparent = false, appearance = "dark", menu = false }) {

  const location = useLocation();
  const [controller, dispatch] = useSoftUIController();
  const { deviceLayout, selectedOrg, userPermissions = [] } = controller;
  var rawMenuItems = getUserMenuItems(userPermissions);

  rawMenuItems.splice(rawMenuItems.indexOf('settings'), 1);
  if (deviceLayout == 'mobile') {
    rawMenuItems.push('settings');
  }

  function menuFilter(route, menuItem) {
    if (route.key == 'settings' && menuItem == 'settings') return true;
    if (!route.in_menu) return false;
    if (!route.key) return false;

    const standardizedKey = route.key.toLowerCase();
    const standardizedType = menuItem.toLowerCase();

    if (standardizedKey === standardizedType) return true;
    if (standardizedKey.startsWith(standardizedType + "-")) return true;
    if (standardizedKey.startsWith(standardizedType + ":")) return true;

    return false;
  }

  const menuItems = rawMenuItems.filter((menuItem) => { // menuItem is key, need to get name in
    const matchingRoutes = routes.filter(r => menuFilter(r, menuItem));

    if (matchingRoutes.length === 0) return false;

    return matchingRoutes.some((route) => {
      if (userPermissions.includes(route.key)) return true;

      if (route.key == 'settings') return true;

      if (route.collapse && route.collapse.length > 0) {
        return route.collapse.some(child => {
          return child.in_menu && userPermissions.includes(child.key);
        });
      }

      return false;
    });
  });

  userAPI.loadToken(useAuth0());

  const [menuAnchor, setMenuAnchor] = useState({
    overview: null,
    devices: null,
    clearways: null,
    crowds: null,
    translations: null,
    media: null,
    operations: null,
    campaign_manager: null,
    kerbsides: null,
  });

  const openMenu = (name) => (domNodeOrEvent) => {
    const anchor = domNodeOrEvent?.currentTarget ? domNodeOrEvent.currentTarget : domNodeOrEvent;
    if (!anchor) return;

    setMenuAnchor((prev) => ({ ...prev, [name]: anchor }));
  };

  const closeMenu = (name) => () => {
    setMenuAnchor((prev) => ({ ...prev, [name]: null }));
  };

  const [mobileNavbar, setMobileNavbar] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const openMobileNavbar = ({ currentTarget }) => setMobileNavbar(currentTarget.parentNode);
  const closeMobileNavbar = () => setMobileNavbar(false);

  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const handleOpenProfileMenu = (event) => setOpenProfileMenu(event.currentTarget);
  const handleCloseProfileMenu = () => setOpenProfileMenu(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    setMobileView(deviceLayout == 'mobile');
    if(deviceLayout != "mobile"){
      setMobileNavbar(false);
    }
  }, [deviceLayout]);

  const NucleusMenuButton = (
    <Box display="flex" sx={{ maxHeight: 'inherit' }}>
      <SoftBox px={0.75} component={Link} to="/" gap={0.6} display="flex" flexDirection="column" justifyContent="center" alignItems="center"
        sx={({ palette, functions: { rgba } }) => ({
          textDecoration: 'none',
          height: '40px !important',
          cursor: "pointer",
          borderRadius: '8px',
          color: location.pathname == "/" ? rgba(palette[appearance].main, 1) : rgba(palette[appearance].text, 1),
          backgroundColor: location.pathname == "/" ? rgba(palette[appearance].text, 1) : rgba(palette[appearance].main, 1),
          opacity: 1,
          '&:hover': {
            opacity: 0,
            color: rgba(palette[appearance].main, 1),
            backgroundColor: rgba(palette[appearance].text, 0.7),
          },
          position: 'relative'
        })}>
        <SoftBox
          pt={0.4}
          component="img"
          src={appearance.includes("light") ? (location.pathname == "/" ? NucleusLogoSmallInverted : NucleusLogoSmall) : (location.pathname == "/" ? NucleusLogoSmall : NucleusLogoSmallInverted)}
          borderRadius="lg"
          sx={{
            height: '20px !important',
            width: '20px !important',
          }}
        />
        <SoftTypography variant="menuCaption" sx={({ palette, functions: { rgba } }) => ({
          color: 'inherit',
          backgroundColor: rgba(palette[appearance].text, 0)
        })}>Nucleus</SoftTypography>
      </SoftBox>
      <SoftBox px={0.75} component={Link} to="/" gap={0.6} display="flex" flexDirection="column" justifyContent="center" alignItems="center"
        sx={({ palette, functions: { rgba } }) => ({
          textDecoration: 'none',
          cursor: "pointer",
          height: '40px !important',
          borderRadius: '8px',
          color: location.pathname == "/" ? rgba(palette[appearance].main, 1) : rgba(palette[appearance].text, 1),
          backgroundColor: location.pathname == "/" ? rgba(palette[appearance].text, 1) : rgba(palette[appearance].main, 1),
          opacity: 0,
          '&:hover': {
            opacity: 1,
            color: rgba(palette[appearance].main, 1),
            backgroundColor: rgba(palette[appearance].text, 0.7),
          },
          position: 'absolute'
        })}>
        <SoftBox
          pt={0.4}
          component="img"
          src={appearance.includes("light") ? NucleusLogoSmallInverted : NucleusLogoSmall}
          borderRadius="lg"
          sx={{
            height: '20px !important',
            width: '20px !important',
          }}
        />
        <SoftTypography variant="menuCaption" sx={({ palette, functions: { rgba } }) => ({
          color: 'inherit',
          backgroundColor: rgba(palette[appearance].text, 0)
        })}>Nucleus</SoftTypography>
      </SoftBox>
    </Box>
  );

  const MenuMenuButton = (
    <SoftBox px={1} py={0.5} gap={0.25} display="flex" flexDirection="column" justifyContent="center" alignItems="center" onClick={openMobileNavbar}
      sx={({ palette, functions: { rgba } }) => ({
        textDecoration: 'none',
        cursor: "pointer",
        borderRadius: '8px',
        color: mobileNavbar ? rgba(palette[appearance].main, 1) : rgba(palette[appearance].text, 1),
        backgroundColor: mobileNavbar ? rgba(palette[appearance].text, 1) : rgba(palette[appearance].main, 1),
        '&:hover': {
          color: rgba(palette[appearance].main, 1),
          backgroundColor: rgba(palette[appearance].text, 0.7),
        }
      })}>
      <Icon fontSize="medium">{mobileNavbar ? "close" : "menu"}</Icon>
      <SoftTypography variant="menuCaption" sx={({ palette, functions: { rgba } }) => ({
        color: 'inherit',
        backgroundColor: rgba(palette[appearance].text, 0)
      })}>Menu</SoftTypography>
    </SoftBox>
  );

  const DeploymentMenuButton = (
    <SoftBox display="flex">
      <SoftBox px={0.5} py={0.5} gap={0.25} display="flex" flexDirection="column" justifyContent="center" alignItems="center" onClick={handleClick}
        sx={({ palette, functions: { rgba } }) => ({
          textDecoration: 'none',
          cursor: "pointer",
          borderRadius: '8px',
          color: open ? rgba(palette[appearance].main, 1) : rgba(palette[appearance].text, 1),
          backgroundColor: open ? rgba(palette[appearance].text, 1) : rgba(palette[appearance].main, 1),
          '&:hover': {
            color: rgba(palette[appearance].main, 1),
            backgroundColor: rgba(palette[appearance].text, 0.7),
          }
        })}>
        <Icon fontSize="medium">workspaces</Icon>
        <SoftTypography variant="menuCaption" sx={({ palette, functions: { rgba } }) => ({
          color: 'inherit',
          textAlign: 'center',
          backgroundColor: rgba(palette[appearance].text, 0),
          lineHeight: 1,
          whiteSpace: 'nowrap',
          textOverflow: "ellipsis",
          overflow: 'hidden',
          display: 'block',
          width: '50px',
        })}>{user.organisations.find(o => o.organisation_id == selectedOrg)?.label}</SoftTypography>
      </SoftBox>
    </SoftBox>
  );

  const SettingsMenuButton = (
    <SoftBox px={0.75} py={1} gap={0.25} display="flex" flexDirection="column" justifyContent="center" alignItems="center" component={Link} to={"/settings"}
      sx={({ palette, functions: { rgba } }) => ({
        textDecoration: 'none',
        cursor: "pointer",
        borderRadius: '8px',
        color: location.pathname == "/settings" ? rgba(palette[appearance].main, 1) : rgba(palette[appearance].text, 1),
        backgroundColor: location.pathname == "/settings" ? rgba(palette[appearance].text, 1) : rgba(palette[appearance].main, 1),
        '&:hover': {
          color: rgba(palette[appearance].main, 1),
          backgroundColor: rgba(palette[appearance].text, 0.7),
        }
      })}>
      <Icon fontSize="medium">settings</Icon>
      <SoftTypography variant="menuCaption" sx={({ palette, functions: { rgba } }) => ({
        color: 'inherit',
        backgroundColor: rgba(palette[appearance].text, 0)
      })}>Settings</SoftTypography>
    </SoftBox>
  );

  return (
    <Container>
      <SoftBox
        py={deviceLayout == 'mobile' ? 0.25 : 1}
        px={deviceLayout == 'mobile' ? 1.5 : 4}
        my={deviceLayout == 'mobile' ? 1 : 2}
        mx={deviceLayout == 'mobile' ? 1 : 2}
        width={`calc(100% - ${deviceLayout == 'mobile' ? '-40px' : '16px'} - (40px * 2))`}
        borderRadius="16px"
        shadow={transparent ? "none" : "md"}
        display="flex"
        justifyContent={deviceLayout == 'mobile' ? "flex-start" : "space-between"}
        alignItems="center"
        position="fixed"
        left={0}
        minHeight={'calc(48px - 4px)'}
        zIndex={1000}
        sx={({ palette, palette: { transparent: transparentColor }, functions: { rgba } }) => ({
          backgroundColor: transparent ? transparentColor.main : rgba(palette[appearance].main, 1),
          backdropFilter: transparent ? "none" : `saturate(200%) blur(30px)`,
          color: rgba(palette[appearance].text, 1),
        })}
      >
        {deviceLayout != 'mobile' && (
          <SoftBox component={Link} to="/" sx={{ textDecoration: 'none', cursor: "pointer", maxWidth: deviceLayout == 'mobile' ? "115px" : "140px" }} pt={deviceLayout == 'mobile' ? '1.5px' : '5px'}>
            <SoftBox
              component="img"
              src={appearance.includes("light") ? NucleusLogo : NucleusLogoInverted}
              width="100%"
              borderRadius="lg"
            />
          </SoftBox>
        )}
        <SoftBox display={{ xxs: "none", xl: "flex" }} m={0} p={0}>
          {menuItems.map((menuItem, itemIndex) => {
            var isSelected = location.pathname.startsWith(routes.filter(r => menuFilter(r, menuItem))?.[0]?.route) || routes.find(r => menuFilter(r, menuItem))?.collapse?.find(r => matchPath({ path: r.route }, location.pathname))?.name || false;

            return (<BaseNavbarLink
              key={itemIndex}
              name={menuItem}
              routes={routes.filter(r => menuFilter(r, menuItem))}
              openHandler={openMenu(menuItem)}
              closeHandler={closeMenu(menuItem)}
              appearance={appearance}
              isSelected={isSelected}
              deviceLayout={deviceLayout}
            />)
          })}
        </SoftBox>
        <SoftBox display={{ xxs: "none", xl: "flex" }} alignItems="center">
          {(user.user.organisations.length > 1 && selectedOrg) && (
            <>
              <SoftBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                onClick={handleClick}
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '8px',
                }}
              >
                <SoftTypography variant="body3" fontWeight="medium" sx={({ palette, functions: { rgba } }) => ({ color: rgba(palette[appearance].text, 1), fontSize: '11px', opacity: 0.7 })}>
                  Viewing:
                </SoftTypography>
                <SoftTypography variant="body2" fontWeight="bold" textTransform="capitalize" sx={({ palette, functions: { rgba } }) => ({ color: rgba(palette[appearance].text, 1), fontSize: '14px', lineHeight: 1.2 })} >
                  {user.organisations.find(row => row.organisation_id == selectedOrg)?.label} ▾
                </SoftTypography>
              </SoftBox>
              {popoverDeployment(open, anchorEl, handleClose, user, dispatch, selectedOrg)}
            </>
          )}
          <SoftBox ml={2}>
            <SoftBox
              py={1}
              px={1.25}
              component={Link}
              to={"/settings"}
              sx={({ palette, functions: { rgba } }) => ({
                cursor: "pointer",
                borderRadius: '12px',
                color: location.pathname == "/settings" ? rgba(palette[appearance].main, 1) : rgba(palette[appearance].text, 1),
                backgroundColor: location.pathname == "/settings" ? rgba(palette[appearance].text, 1) : rgba(palette[appearance].main, 1),
                '&:hover': {
                  color: rgba(palette[appearance].main, 1),
                  backgroundColor: rgba(palette[appearance].text, 0.7),
                },
                display: 'flex'
              })}>
              <Icon fontSize="default" sx={{ verticalAlign: 'middle' }}>settings</Icon>
            </SoftBox>
          </SoftBox>
        </SoftBox>
        <SoftBox
          display={{ xxs: "flex", xl: "none" }}
          alignItems="center" // Add this to ensure vertical centering
          width="100%"
          lineHeight={0}
          px={{ xxs: 0, md: 2 }}
          color="inherit"
          sx={{ maxHeight: '40px !important' }}
        >
          {/* LEFT SIDE: Takes up exactly half of the remaining space */}
          <SoftBox display="flex" flex={1} justifyContent="flex-start">
            {MenuMenuButton}
          </SoftBox>

          {/* TRUE CENTER: Logo */}
          {deviceLayout == 'mobile' && (
            <SoftBox
              component={Link}
              to="/"
              alignContent="center"
              sx={{ textDecoration: 'none', cursor: "pointer", maxWidth: deviceLayout == 'mobile' ? "115px" : "140px" }}
              pt={deviceLayout == 'mobile' ? '1.5px' : '5px'}
              px={3}
            >
              <SoftBox
                component="img"
                src={appearance.includes("light") ? NucleusLogo : NucleusLogoInverted}
                width="100%"
                borderRadius="lg"
              />
            </SoftBox>
          )}

          {/* RIGHT SIDE: Takes up exactly half of the remaining space */}
          <SoftBox display="flex" flex={1} justifyContent="flex-end">
            {(user.user.organisations.length > 1 && selectedOrg) && (
              <>
                {DeploymentMenuButton}
                {popoverDeployment(open, anchorEl, handleClose, user, dispatch, selectedOrg)}
              </>
            )}
          </SoftBox>
        </SoftBox>
      </SoftBox>

      {
        menuItems.map((menuItem, itemIndex) => {

          return (<MenuOptions
            key={itemIndex}
            routes={routes.filter(r => menuFilter(r, menuItem))}
            route_path={menuItem}
            open={menuAnchor[menuItem]}
            close={closeMenu(menuItem)}
            openHandler={openMenu(menuItem)}
            noCollapse={routes.filter(r => r?.key && r?.key.includes(menuItem) && !r.collapse).length > 0}
          />)
        })
      }
      {
        mobileView && (
          <BaseNavbarMobile routes={routes} menuItems={menuItems} open={mobileNavbar} close={closeMobileNavbar} />
        )
      }
    </Container >
  );
}

BaseNavbar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  user: PropTypes.object,
  transparent: PropTypes.bool,
  appearance: PropTypes.string,
  menu: PropTypes.bool,
};

export default BaseNavbar;